import { useState, useMemo } from "react";
import AddOrganizationWizard from "@/components/corporate/organizations/AddOrganizationWizard";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Search,
  Eye,
  ChevronUp,
  ChevronDown,
  Building2,
  Calendar,
  Plus
} from "lucide-react";

import type { Organization } from "@/types/organizations";
import { useOrganizations } from "@/hooks/useOrganizations";

type SortField = 'name' | 'subdomain' | 'status' | 'createdAt';
type SortOrder = 'asc' | 'desc';

interface OrganizationFilters {
  search: string;
  status: string;
  isWhiteLabel: string;
}

const getStatusColor = (isActive: boolean) => {
  return isActive 
    ? "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400"
    : "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

export default function Organizations() {
  const [, navigate] = useLocation();
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [filters, setFilters] = useState<OrganizationFilters>({
    search: '',
    status: 'all',
    isWhiteLabel: 'all'
  });
  const [showAddOrganizationWizard, setShowAddOrganizationWizard] = useState(false);

  // Fetch organizations from API
  const { organizations, isLoading } = useOrganizations();

  // Filter and sort organizations
  const filteredAndSortedOrganizations = useMemo(() => {
    let filtered = organizations.filter(organization => {
      const matchesSearch = organization.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        organization.subdomain.toLowerCase().includes(filters.search.toLowerCase());
      const matchesStatus = filters.status === 'all' ||
        (filters.status === 'active' && organization.isActive) ||
        (filters.status === 'inactive' && !organization.isActive);
      const matchesWhiteLabel = filters.isWhiteLabel === 'all' ||
        (filters.isWhiteLabel === 'yes' && organization.isWhiteLabel) ||
        (filters.isWhiteLabel === 'no' && !organization.isWhiteLabel);

      return matchesSearch && matchesStatus && matchesWhiteLabel;
    });

    return filtered.sort((a, b) => {
      let aValue: any, bValue: any;

      // Map sortField to actual API fields
      if (sortField === 'createdAt') {
        aValue = new Date(a.createdAt).getTime();
        bValue = new Date(b.createdAt).getTime();
      } else if (sortField === 'status') {
        aValue = a.isActive ? 'active' : 'inactive';
        bValue = b.isActive ? 'active' : 'inactive';
      } else {
        aValue = a[sortField];
        bValue = b[sortField];
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [organizations, filters, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrganizations(filteredAndSortedOrganizations.map(organization => organization.id));
    } else {
      setSelectedOrganizations([]);
    }
  };

  const handleSelectOrganization = (organizationId: string, checked: boolean) => {
    if (checked) {
      setSelectedOrganizations([...selectedOrganizations, organizationId]);
    } else {
      setSelectedOrganizations(selectedOrganizations.filter(id => id !== organizationId));
    }
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <TableHead 
      className="cursor-pointer hover:bg-muted hover:bg-opacity-50 transition-colors"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-2">
        {children}
        <div className="flex flex-col">
          <ChevronUp 
            className={`w-3 h-3 ${sortField === field && sortOrder === 'asc' ? 'text-primary' : 'text-muted-foreground'}`} 
          />
          <ChevronDown 
            className={`w-3 h-3 -mt-1 ${sortField === field && sortOrder === 'desc' ? 'text-primary' : 'text-muted-foreground'}`} 
          />
        </div>
      </div>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header
          title="Organizations"
          description="Manage and monitor all client organizations"
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading organizations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full">
        <Header
          title="Organizations"
          description="Manage and monitor all client organizations"
        >
          <Button 
            onClick={() => setShowAddOrganizationWizard(true)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Organization
          </Button>
        </Header>
        
        <div className="flex-1 overflow-auto p-6">
          {/* Search and Filters */}
          <Card className="athlyn-card mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search organizations..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10"
                  />
                </div>

                {/* Status Filter */}
                <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                  <SelectTrigger className="w-full lg:w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>

                {/* White Label Filter */}
                <Select value={filters.isWhiteLabel} onValueChange={(value) => setFilters({ ...filters, isWhiteLabel: value })}>
                  <SelectTrigger className="w-full lg:w-40">
                    <SelectValue placeholder="White Label" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="yes">White Label</SelectItem>
                    <SelectItem value="no">Standard</SelectItem>
                  </SelectContent>
                </Select>

              </div>
            </CardContent>
          </Card>

          {/* Organizations Table */}
          {filteredAndSortedOrganizations.length > 0 ? (
            <Card className="athlyn-card">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedOrganizations.length === filteredAndSortedOrganizations.length}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all organizations"
                        />
                      </TableHead>
                      <SortableHeader field="name">Organization Name</SortableHeader>
                      <SortableHeader field="subdomain">Subdomain</SortableHeader>
                      <SortableHeader field="status">Status</SortableHeader>
                      <SortableHeader field="createdAt">Created</SortableHeader>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAndSortedOrganizations.map((organization) => (
                      <TableRow 
                        key={organization.id}
                        className={`hover:bg-muted hover:bg-opacity-50 transition-colors cursor-pointer ${
                          selectedOrganizations.includes(organization.id) ? 'bg-primary bg-opacity-10' : ''
                        }`}
                        onClick={(e) => {
                          // Don't navigate if clicking on interactive elements
                          const target = e.target as HTMLElement;
                          const isInteractive = target.closest('button, input, a, [role="checkbox"], [role="menuitem"]');
                          if (!isInteractive) {
                            navigate(`/organizations/${organization.id}`);
                          }
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedOrganizations.includes(organization.id)}
                            onCheckedChange={(checked) => handleSelectOrganization(organization.id, !!checked)}
                            aria-label={`Select ${organization.name}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {organization.logo?.url ? (
                              <img
                                src={organization.logo.url}
                                alt={organization.name}
                                className="w-8 h-8 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-8 h-8 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                                <Building2 className="w-4 h-4 text-primary" />
                              </div>
                            )}
                            <div>
                              <p className="font-semibold text-foreground">{organization.name}</p>
                              <p className="text-sm text-muted-foreground">{organization.subdomain}.athlyn.app</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium text-muted-foreground">
                          {organization.subdomain}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={`${getStatusColor(organization.isActive)} text-xs`}>
                              {organization.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                            {organization.isWhiteLabel && (
                              <Badge variant="secondary" className="text-xs">
                                White Label
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(organization.createdAt)}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-8 h-8 p-0"
                                  onClick={() => navigate(`/organizations/${organization.id}`)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>View Details</TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          ) : (
            // Empty State
            <Card className="athlyn-card">
              <CardContent className="p-12 text-center">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">No organizations match your criteria</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters or search terms to find the organizations you're looking for.
                </p>
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2"
                  onClick={() => setShowAddOrganizationWizard(true)}
                >
                  <Plus className="w-4 h-4" />
                  Add New Organization
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Add Organization Wizard */}
      <AddOrganizationWizard
        isOpen={showAddOrganizationWizard}
        onClose={() => setShowAddOrganizationWizard(false)}
      />
    </TooltipProvider>
  );
}